// 2FA Authentication Utility
const STORAGE_KEY = "fb11_2fa";
const LOCKOUT_KEY = "fb11_lockout";
const MAX_ATTEMPTS = 3;
const LOCKOUT_MINUTES = 5;

// Get 2FA config
export function get2FAConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

// Save 2FA config
function save2FAConfig(config) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

// Check if 2FA is enabled for a user
export function is2FAEnabled(userId) {
  const config = get2FAConfig();
  return !!(config[userId] && config[userId].enabled);
}

// Setup 2FA PIN for a user
export function setup2FA(userId, pin) {
  if (!pin || pin.length < 4 || pin.length > 6) {
    return { success: false, error: "PIN must be 4-6 digits" };
  }
  if (!/^\d+$/.test(pin)) {
    return { success: false, error: "PIN must be numbers only" };
  }
  const config = get2FAConfig();
  // Simple hash for PIN storage (not plain text)
  const hash = btoa(pin.split("").reverse().join("") + "_fb11_" + userId);
  config[userId] = { enabled: true, hash, setupAt: new Date().toISOString() };
  save2FAConfig(config);
  return { success: true };
}

// Verify 2FA PIN
export function verify2FA(userId, pin) {
  // Check lockout
  const lockout = getLockout(userId);
  if (lockout.locked) {
    return { success: false, error: "Account locked. Try again in " + lockout.remaining + " min", locked: true };
  }

  const config = get2FAConfig();
  if (!config[userId]) return { success: false, error: "2FA not configured" };

  const expected = btoa(pin.split("").reverse().join("") + "_fb11_" + userId);
  if (config[userId].hash === expected) {
    clearAttempts(userId);
    return { success: true };
  } else {
    const attempts = addAttempt(userId);
    const remaining = MAX_ATTEMPTS - attempts;
    if (remaining <= 0) {
      setLockout(userId);
      return { success: false, error: "Too many attempts. Locked for " + LOCKOUT_MINUTES + " min", locked: true };
    }
    return { success: false, error: "Wrong PIN. " + remaining + " attempt(s) left" };
  }
}

// Disable 2FA
export function disable2FA(userId) {
  const config = get2FAConfig();
  delete config[userId];
  save2FAConfig(config);
}

// Attempt tracking
function addAttempt(userId) {
  const key = "fb11_2fa_attempts_" + userId;
  const attempts = (parseInt(localStorage.getItem(key)) || 0) + 1;
  localStorage.setItem(key, attempts.toString());
  return attempts;
}

function clearAttempts(userId) {
  localStorage.removeItem("fb11_2fa_attempts_" + userId);
}

// Lockout
function setLockout(userId) {
  const until = Date.now() + LOCKOUT_MINUTES * 60 * 1000;
  localStorage.setItem(LOCKOUT_KEY + "_" + userId, until.toString());
  clearAttempts(userId);
}

function getLockout(userId) {
  const until = parseInt(localStorage.getItem(LOCKOUT_KEY + "_" + userId));
  if (!until) return { locked: false };
  if (Date.now() > until) {
    localStorage.removeItem(LOCKOUT_KEY + "_" + userId);
    return { locked: false };
  }
  const remaining = Math.ceil((until - Date.now()) / 60000);
  return { locked: true, remaining };
}

// Change PIN
export function change2FAPIN(userId, oldPin, newPin) {
  const verify = verify2FA(userId, oldPin);
  if (!verify.success) return verify;
  return setup2FA(userId, newPin);
}
